import { SearchQueryType } from "models/enumerations/search-query-type";
import SearchSortOrderBy from "models/enumerations/sorting/search-results/search-sort-order-by";
import HitRecord from "models/view-models/search/hit-record";
import { useQuery } from "react-query";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { useEvents } from "utilities/contexts/events/events-provider";
import useOfflineSearchService from "utilities/hooks/aspect/search/offline/use-offline-search-service";
import useRelatedRecords from "utilities/hooks/aspect/search/use-related-records";
import usePublications from "utilities/hooks/domain/publications/use-publications";
import SearchService, {
    SearchListQueryParams,
} from "utilities/services/search/search-service";
import StringUtils from "utilities/string-utils";

const BOOKVIEW_SEARCH_QUERY_KEY = "bookview-search-query-key";

export const useBookviewSearchQuery = (
    searchParams: SearchListQueryParams,
    disableSearch = false
) => {
    const { get: searchOnlineApi } = SearchService.useGet();
    const { get: searchOfflineApi } = useOfflineSearchService();
    const { isOnline } = useNetworkInformation();
    const { resultObject: publications } = usePublications();
    const { createSearchSubmittedEvent } = useEvents();

    const { getRelatedRecords } = useRelatedRecords();

    const fetchOnlineSearchResults = async (): Promise<{
        searchResults: HitRecord[];
        rowCount: number | undefined;
    }> => {
        const start = Date.now();
        const searchResultResponse = await searchOnlineApi(
            {},
            {
                ...searchParams,
                searchType: SearchQueryType.Simple,
                sortOrder: SearchSortOrderBy.PrintOrder,
            }
        );

        const elapsed = Date.now() - start;
        createSearchSubmittedEvent(
            searchParams,
            searchResultResponse.resultObject?.found ?? 0,
            elapsed
        );

        const hits = searchResultResponse.resultObject?.hits ?? [];
        const hitsWithRelatedRecords = await getRelatedRecords(hits);
        return {
            searchResults: hitsWithRelatedRecords,
            rowCount: searchResultResponse.resultObject?.found,
        };
    };

    const fetchOfflineSearchResults = async (): Promise<{
        searchResults: HitRecord[];
        rowCount: number | undefined;
    }> => {
        const searchResultResponse = await searchOfflineApi({
            ...searchParams,
            sortOrder: SearchSortOrderBy.PrintOrder,
        });
        const hits = searchResultResponse.resultObjects;
        const hitsWithPublicationInfo = hits.map(
            (h) => h.withPublication(publications) // Needed for search result breadcrumb text
        );
        return {
            searchResults: hitsWithPublicationInfo,
            rowCount: searchResultResponse.rowCount,
        };
    };
    const {
        data,
        refetch,
        isLoading,
        isFetching,
        isError,
        remove: reset,
    } = useQuery({
        queryKey: [
            BOOKVIEW_SEARCH_QUERY_KEY,
            searchParams?.search,
            searchParams.start,
            searchParams?.articleIds,
            searchParams?.chapterIds,
            searchParams?.annexIds,
            searchParams?.publicationIds,
            searchParams?.entityTypes,
        ],
        queryFn: isOnline
            ? fetchOnlineSearchResults
            : fetchOfflineSearchResults,
        enabled: StringUtils.hasValue(searchParams.search) && !disableSearch,
        keepPreviousData: !disableSearch,
        staleTime: Infinity,
    });

    return {
        searchResults: data?.searchResults,
        rowCount: data?.rowCount,
        isLoading: isLoading || isFetching,
        isError,
        refetchResults: refetch,
        reset,
    };
};
